import { content } from '../content';
import { BlogBlock, BlogPost } from '../types';
import { withPlural } from './plural';

/**
 * "2026-08-21" → "21 августа 2026". Stored absolute and rendered absolute:
 * "3 дня назад" is wrong the week after it is written, and a stale relative
 * date makes recent work look abandoned.
 */
export const formatPostDate = (iso: string): string | null => {
  const date = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;

  return date
    .toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    })
    .replace(/\s*г\.$/, '');
};

/** Everything in a block that a reader actually reads. */
const wordsIn = (block: BlogBlock): number => {
  const count = (text: string) => text.trim().split(/\s+/).filter(Boolean).length;

  switch (block.type) {
    case 'text':
    case 'heading':
      return count(block.text);
    case 'quote':
      return count(block.text);
    case 'note':
      return count(block.text) + (block.title ? count(block.title) : 0);
    case 'list':
      return block.items.reduce((sum, item) => sum + count(item), 0);
    case 'compare':
      return [...block.before.items, ...block.after.items].reduce(
        (sum, item) => sum + count(item),
        0
      );
    case 'stats':
      return block.items.reduce((sum, item) => sum + count(item.label), 0);
    // Code is scanned rather than read, and a picture is not read at all —
    // counting either would make a short post claim to be a long one.
    default:
      return 0;
  }
};

/** "7 минут чтения". `readingMinutes` on the post overrides the estimate. */
export const readingTime = (post: BlogPost): string => {
  const words = post.body.reduce((sum, block) => sum + wordsIn(block), 0);
  const minutes =
    post.readingMinutes ?? Math.max(1, Math.round(words / content.ui.blogWpm));

  return content.ui.blogReading(withPlural(minutes, content.ui.blogMinutesPlural));
};

/** Newest first, whatever order the posts were typed into `content.ts`. */
export const sortedPosts = (posts: BlogPost[]): BlogPost[] =>
  [...posts].sort((a, b) => b.date.localeCompare(a.date));
